/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2024-09-11
 * @description Manage the documents details.
 */
import { Box, Button, Skeleton, Stack } from "@mui/material";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";

import { HeaderSection } from "./HeaderSection";
import { FormSection } from "./FormSection";
import { DetailsPanel } from "./DetailsPanel";
import { useFetchData } from "../../../../components/hooks/useFetchData";
import { LoadingBackdrop } from "../../../../components/common/LoadingBackdrop";

const ManageDocument = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const document = location.state?.document;
  const { enqueueSnackbar } = useSnackbar();
  const { fetchData, loading } = useFetchData();
  const [minLoading, setMinLoading] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      documentid: document?.id || 0,
      documentname: document?.documentname || "",
      isactive: document?.isactive || 0,
    },
  });

  const handleReset = () => {
    reset({
      documentid: document.id || 0,
      documentname: document.documentname || "",
      isactive: document.isactive || 0,
    });
  };

  const onSubmit = async (data) => {
    const body = {
      documents: [
        {
          documentid: document?.id || 0,
          documentname: data.documentname,
          isactive: data.isactive,
        },
      ],
    };
    const result = await fetchData("managedocument", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      navigate(-1);
    } else {
      enqueueSnackbar(result.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  React.useEffect(() => {
    const minLoadingTimer = setTimeout(() => {
      setMinLoading(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
  }, []);

  const isLoading = minLoading;

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        overflow={{ xs: "auto", sm: "hidden" }}
      >
        <HeaderSection isLoading={isLoading} />
        <Stack
          pt={3}
          flex={1}
          overflow={{ xs: "unset", sm: "hidden" }}
          direction={{ xs: "column", sm: "row" }}
          columnGap={1}
          rowGap={2}
        >
          <FormSection
            isLoading={isLoading}
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
          />
          <DetailsPanel />
        </Stack>
        <Stack
          width="100%"
          mt={2}
          direction="row"
          alignItems="center"
          justifyContent="end"
          spacing={2}
        >
          {isLoading ? (
            <Skeleton variant="rounded" width={160} height={40} />
          ) : (
            <Button
              onClick={handleReset}
              variant="outlined"
              sx={{ borderRadius: "12px", minWidth: 160, py: 1.2 }}
            >
              Reset
            </Button>
          )}
          {isLoading ? (
            <Skeleton variant="rounded" width={160} height={40} />
          ) : (
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              sx={{ borderRadius: "12px", minWidth: 160, py: 1.2 }}
            >
              Save
            </Button>
          )}
        </Stack>
      </Box>
      <LoadingBackdrop isLoading={loading["managedocument"]} />
    </>
  );
};

export default ManageDocument;
